import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { LogOut, UserCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useSuplantacion } from "@/lib/sesion";

/**
 * Banda permanente durante una suplantación.
 *
 * El requerimiento pide que en toda la navegación quede claro que la sesión no
 * es la propia. Va pegada arriba de todo, con color de advertencia, y no se
 * puede cerrar: la única forma de quitarla es salir de la suplantación.
 */
export function BandaSuplantacion() {
  const { suplantacion, refrescar } = useSuplantacion();
  const qc = useQueryClient();
  const [saliendo, setSaliendo] = useState(false);

  if (!suplantacion.activa) return null;

  const salir = async () => {
    setSaliendo(true);
    try {
      await api.post("/api/suplantacion/terminar");
      await refrescar();
      // Todo lo cacheado se resolvió con la identidad del suplantado: contextos,
      // bandejas, permisos. Al recuperar la sesión propia nada de eso sirve.
      await qc.invalidateQueries();
    } finally {
      setSaliendo(false);
    }
  };

  return (
    <div
      role="status"
      className="sticky top-0 z-40 flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-estado-warn/40 bg-estado-warn-bg px-4 py-2 text-sm text-estado-warn sm:px-6"
    >
      <UserCheck className="h-4 w-4 shrink-0" />
      <span className="flex-1">
        Estás navegando como{" "}
        <strong>{suplantacion.suplantadoNombre ?? suplantacion.suplantadoCorreo}</strong>
        {suplantacion.suplantadoCorreo && suplantacion.suplantadoNombre && (
          <span className="ml-1 opacity-80">({suplantacion.suplantadoCorreo})</span>
        )}
        . Lo que hagas queda registrado a tu nombre como{" "}
        <strong>{suplantacion.adminCorreo}</strong>.
      </span>
      <button
        onClick={salir}
        disabled={saliendo}
        className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded border border-estado-warn/50 bg-surface px-3 py-1 text-xs font-semibold transition hover:bg-estado-warn/10 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <LogOut className="h-3.5 w-3.5" />
        {saliendo ? "Saliendo…" : "Salir de suplantación"}
      </button>
    </div>
  );
}
